import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../Layout/LanguageContext';
import { 
  FaEnvelope, 
  FaPhone,
  FaMapMarkerAlt
} from 'react-icons/fa';

const ContactSection = styled.section`
  padding: 8rem 2rem;
  background: var(--background-light);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 100px;
  min-height: 100vh;

  @media (max-width: 1200px) {
    padding: 6rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    min-height: auto;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;

  @media (max-width: 1200px) {
    margin-bottom: 4rem;
  }

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: flex-start;

  @media (max-width: 1200px) {
    gap: 4rem;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2.5rem;
  background: var(--background-color);
  border: 1px solid var(--background-dark);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 51, 102, 0.1);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--background-light);
  border-radius: 12px;
  border: 1px solid var(--background-dark);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 51, 102, 0.1);
    border-color: var(--accent-color);
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ContactText = styled.div`
  flex: 1;
`;

const ContactLabel = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0 0 0.25rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const ContactValue = styled.p`
  font-size: 1rem;
  color: var(--primary-color);
  margin: 0;
  font-weight: 500;
`;

const ContactLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent-color);
  }
`;

const FormContainer = styled.div`
  background: var(--background-color);
  padding: 2.5rem;
  border-radius: 16px;
  border: 1px solid var(--background-dark);
  box-shadow: 0 8px 32px rgba(0, 51, 102, 0.1);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 1rem;
  border: 1px solid var(--background-dark);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--background-light);
  color: var(--text-color);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.1);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border: 1px solid var(--background-dark);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--background-light);
  color: var(--text-color);
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-color);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.1);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 51, 102, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 51, 102, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: var(--success);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
`;

const ErrorMessage = styled(motion.div)`
  background: var(--error);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 6px;
  height: 6px;
  background: var(--accent-color);
  border-radius: 50%;
  opacity: 0.4;

  &:nth-child(1) { top: 15%; left: 10%; }
  &:nth-child(2) { top: 25%; right: 15%; }
  &:nth-child(3) { top: 75%; left: 20%; }
  &:nth-child(4) { top: 85%; right: 25%; }
`;

const contactContent = {
  de: {
    title: 'Kontakt',
    subtitle: 'Lassen Sie uns gemeinsam Ihr Projekt verwirklichen',
    description: 'Ich freue mich darauf, von Ihnen zu hören und Ihnen bei Ihren IT-Projekten zu helfen.',
    form: {
      name: 'Name',
      email: 'E-Mail',
      subject: 'Betreff',
      message: 'Nachricht',
      send: 'Nachricht senden',
      sending: 'Wird gesendet...',
      success: 'Nachricht erfolgreich gesendet!',
      error: 'Fehler beim Senden der Nachricht.'
    },
    info: {
      title: 'Kontaktinformationen',
      address: 'Adresse',
      addressValue: 'Sonthofen, Deutschland',
      phone: 'Telefon',
      phoneValue: '+491604296531',
      email: 'E-Mail',
      emailValue: 'kontakt@it-beratung-reinke.de'
    }
  },
  en: {
    title: 'Contact',
    subtitle: 'Let\'s work together to realize your project',
    description: 'I look forward to hearing from you and helping you with your IT projects.',
    form: {
      name: 'Name',
      email: 'Email',
      subject: 'Subject',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Message sent successfully!',
      error: 'Error sending message.'
    },
    info: {
      title: 'Contact Information',
      address: 'Address',
      addressValue: 'Sonthofen, Germany',
      phone: 'Phone',
      phoneValue: '+491604296531',
      email: 'Email',
      emailValue: 'kontakt@it-beratung-reinke.de'
    }
  }
};

const Contact: React.FC = () => {
  const { language } = useLanguage();
  const t = contactContent[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // EmailJS-Konfiguration
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_name: 'IT-Beratung Reinke'
      };

      // EmailJS senden
      const result = await emailjs.send(
        'service_5z6jcxc', // Deine EmailJS Service ID
        'template_f6fs7af', // Deine EmailJS Template ID
        templateParams,
        'IYl5Y3wLPVWzivxcq' // Dein EmailJS Public Key
      );

      if (result.status === 200) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <Container>
        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <ContentWrapper>
          <ContactInfo>
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>
                <ContactLabel>{t.info.address}</ContactLabel>
                <ContactValue>{t.info.addressValue}</ContactValue>
              </ContactText>
            </ContactItem>

            <ContactItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactText>
                <ContactLabel>{t.info.email}</ContactLabel>
                <ContactLink href={`mailto:${t.info.emailValue}`}>
                  {t.info.emailValue}
                </ContactLink>
              </ContactText>
            </ContactItem>

            <ContactItem>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactText>
                <ContactLabel>{t.info.phone}</ContactLabel>
                <ContactLink href={`tel:${t.info.phoneValue}`}>
                  {t.info.phoneValue}
                </ContactLink>
              </ContactText>
            </ContactItem>
          </ContactInfo>

          <FormContainer>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">{t.form.name} *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t.form.name}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">{t.form.email} *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={t.form.email}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">{t.form.subject}</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={t.form.subject}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">{t.form.message} *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder={t.form.message}
                />
              </FormGroup>

              <SubmitButton
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? t.form.sending : t.form.send}
              </SubmitButton>

              <AnimatePresence>
                {submitStatus === 'success' && (
                  <SuccessMessage
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    {t.form.success}
                  </SuccessMessage>
                )}

                {submitStatus === 'error' && (
                  <ErrorMessage
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    {t.form.error}
                  </ErrorMessage>
                )}
              </AnimatePresence>
            </Form>
          </FormContainer>
        </ContentWrapper>

        <FloatingElement
          animate={{
            y: [0, -15, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 20, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, -10, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 25, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />
      </Container>
    </ContactSection>
  );
};

export default Contact; 